/**
 * FinalizationStep Component
 *
 * Step 6: Update complete - show success and next steps
 * Displays success message and provides options for the user.
 */

import { Button } from '@ui/shared/Button';
import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { Icon } from '../../../../shared/Icon';
import AppStoreBadge from '../../../../../../images/app_store_badge.svg';
import AppStoreQR from '../../../../../../images/app_store_qr.png';
import GooglePlayBadge from '../../../../../../images/google_play_badge.svg';
import GooglePlayQR from '../../../../../../images/google_play_qr.png';
import { useWizard } from '../../hooks/useWizard';
import { useUpdaterServices } from '../../hooks/useUpdaterServices';
import { HelpFooter } from '../HelpFooter';
import { StateActionType } from '../../types/wizard-types';

/**
 * Props for FinalizationStep
 */
export interface FinalizationStepProps {
  onComplete?: () => void;
  onClose?: () => void;
}

/**
 * FinalizationStep component
 */
export const FinalizationStep: React.FC<FinalizationStepProps> = ({
  onComplete,
  onClose,
}: FinalizationStepProps): React.ReactElement => {
  const { state, dispatch, reset } = useWizard();
  const { logger } = useUpdaterServices();

  // Mark wizard as complete on mount
  React.useEffect((): void => {
    if (!state.completed) {
      dispatch({ type: StateActionType.COMPLETE });
    }
  }, [state.completed, dispatch]);

  /**
   * Handle update another device
   */
  const handleUpdateAnother = (): void => {
    reset();
  };

  /**
   * Handle done - call onComplete callback and close the dialog
   */
  const handleDone = (): void => {
    if (onComplete) {
      onComplete();
    }
    if (onClose) {
      onClose();
    }
  };

  /**
   * Handle download logs
   */
  const handleDownloadLogs = (): void => {
    logger.downloadLogs(state.eid || 'unknown');
  };

  return (
    <StepContainer>
      {/* Success Animation */}
      <SuccessSection>
        <SuccessIcon>
          <AnimatedCheckmark>
            <Icon name='check' color='white' size='small' />
          </AnimatedCheckmark>
        </SuccessIcon>
        <SuccessContent>
          <SuccessTitle>Update Complete!</SuccessTitle>
          <SuccessMessage>
            Your plates have been successfully updated to the latest firmware.
          </SuccessMessage>
        </SuccessContent>
        <DeviceInfoInline>
          <DeviceInfoLabel>Equipment ID:</DeviceInfoLabel>
          <DeviceInfoValue>{state.eid || 'N/A'}</DeviceInfoValue>
        </DeviceInfoInline>
      </SuccessSection>

      {/* Next Steps with App Download on the side */}
      <NextStepsSection>
        <NextStepsContent>
          <NextStepsTitle>Next Steps</NextStepsTitle>
          <NextStepsList>
            <NextStepItem>
              <NextStepNumber>1</NextStepNumber>
              <NextStepContent>
                <NextStepLabel>Disconnect the plates</NextStepLabel>
                <NextStepDescription>
                  Safely remove the USB-C cable from your force plates.
                </NextStepDescription>
              </NextStepContent>
            </NextStepItem>
            <NextStepItem>
              <NextStepNumber>2</NextStepNumber>
              <NextStepContent>
                <NextStepLabel>Power on your plates</NextStepLabel>
                <NextStepDescription>
                  Power the force plates back on.
                </NextStepDescription>
              </NextStepContent>
            </NextStepItem>
            <NextStepItem>
              <NextStepNumber>3</NextStepNumber>
              <NextStepContent>
                <NextStepLabel>
                  Get the latest version of Hawkin Capture
                </NextStepLabel>
                <NextStepDescription>
                  Scan a QR code to download the app for your device.
                </NextStepDescription>
              </NextStepContent>
            </NextStepItem>
          </NextStepsList>
        </NextStepsContent>
        <AppStoreLinks>
          <AppStoreLink>
            <QRCodeWrapper>
              <QRCodeImage src={AppStoreQR} alt='App Store QR Code' />
            </QRCodeWrapper>
            <StoreBadge src={AppStoreBadge} alt='Download on the App Store' />
          </AppStoreLink>
          <AppStoreLink>
            <QRCodeWrapper>
              <QRCodeImage src={GooglePlayQR} alt='Google Play QR Code' />
            </QRCodeWrapper>
            <StoreBadge src={GooglePlayBadge} alt='Get it on Google Play' />
          </AppStoreLink>
        </AppStoreLinks>
      </NextStepsSection>

      {/* Action Buttons */}
      <ActionSection>
        <PrimaryActions>
          <Button
            icon='check'
            title='Done'
            flavor='primary'
            onClick={handleDone}
          />
          <Button
            icon='add'
            title='Update Another Set of Plates'
            flavor='secondary'
            onClick={handleUpdateAnother}
          />
        </PrimaryActions>
        <SecondaryActions>
          <TextButton onClick={handleDownloadLogs}>
            <Icon name='download' size='tiny' />
            <span>Download Update Log</span>
          </TextButton>
        </SecondaryActions>
      </ActionSection>

      {/* Support Info */}
      <HelpFooter
        message='Having issues with your updated plates?'
        iconName='support_agent'
      />
    </StepContainer>
  );
};

// Animations

const scaleIn = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const checkmarkDraw = keyframes`
  0% {
    opacity: 0;
    transform: scale(0) rotate(-45deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.2) rotate(0deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
`;

// Styled Components

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SuccessSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #66bb6a 0%, #4caf50 100%);
  border-radius: 10px;
`;

const SuccessContent = styled.div`
  flex: 1;
`;

const SuccessIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  margin-bottom: 8px;
  animation: ${scaleIn} 0.5s ease-out;
`;

const AnimatedCheckmark = styled.div`
  animation: ${checkmarkDraw} 0.5s ease-out 0.2s both;
`;

const SuccessTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  color: white;
`;

const SuccessMessage = styled.p`
  margin: 4px 0 0 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
`;

const DeviceInfoLabel = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
`;

const DeviceInfoValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: white;
`;

const DeviceInfoInline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 6px;
`;

const NextStepsSection = styled.div`
  display: flex;
  gap: 24px;
  background: var(--color-surface-1);
  border-radius: 10px;
  padding: 20px;
`;

const NextStepsContent = styled.div`
  flex: 0 0 auto;
  max-width: 400px;
`;

const NextStepsTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
`;

const NextStepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NextStepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const NextStepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--color-primary);
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  color: white;
  flex-shrink: 0;
`;

const NextStepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NextStepLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text);
`;

const NextStepDescription = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.4;
`;

const AppStoreLinks = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  margin-left: 24px;
`;

const AppStoreLink = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const QRCodeWrapper = styled.div`
  background: white;
  border-radius: 8px;
  padding: 8px;
`;

const QRCodeImage = styled.img`
  display: block;
  width: 100px;
  height: 100px;
`;

const StoreBadge = styled.img`
  height: 40px;
  width: auto;
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
`;

const SecondaryActions = styled.div`
  display: flex;
  gap: 16px;
`;

const ButtonText = styled.span`
  margin-left: 8px;
`;

const TextButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: none;
  border: none;
  font-size: 13px;
  color: var(--color-disabled-text);
  cursor: pointer;
  border-radius: 6px;
  transition: all 200ms ease;

  &:hover {
    background: var(--color-surface-1);
    color: var(--color-text);
  }
`;

export default FinalizationStep;
